import React, { useContext, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trash2, Plus, Loader2, Info } from 'lucide-react';
import { useMutation } from '@apollo/client';

import dayjs from 'utils/extendedDayJS';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Input } from '@epoch-ui/components/Input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@epoch-ui/components/AlertDialog';
import {
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage
} from '@epoch-ui/components/Form';
import {
  AlertBanner,
  AlertBannerDescription,
  AlertBannerIcon
} from '@epoch-ui/components/AlertBanner';
import {
  ZoomConnectionModal,
  GoogleMeetConnectionModal
} from 'epoch-components/VirtualMeetingConnectionModals';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import UserContext from 'context/userContext';
import {
  CREATE_ZOOM_LINK,
  CREATE_MEET_LINK,
  REMOVE_ZOOM_LINK,
  REMOVE_MEET_LINK
} from 'graphql/mutations/events/edit-event';
import { isGoogleMeetLinkUsed, isZoomLinkUsed } from 'graphql/queries/events/check-meeting-link';
import {
  DEFAULT_LINK_GENERATION_ERROR,
  MEET_REINTEGRATION_ERROR,
  ZOOM_REINTEGRATION_ERROR
} from 'constants/strings';

enum ModalType {
  ZOOM = 'zoom',
  MEET = 'meet'
}

enum ModalMode {
  CONNECT = 'connect',
  RECONNECT = 'reconnect'
}

type MeetingFieldNames = Record<'zoom' | 'googleMeet', string>;

interface EventVirtualLocationFieldProps {
  name: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  meetingIdFieldNames?: MeetingFieldNames;
  eventId?: number;
  timezoneCode?: string;
}

const DefaultLabel = (
  <>
    Virtual link <span className="tw-text-gray-500">(optional)</span>
  </>
);

const DefaultDescription = (
  <span className="tw-mt-1">
    Generate a meeting link to automate attendance tracking.{' '}
    <a
      href="https://help.epochapp.com/articles/attendance-tracking-virtual-events"
      target="_blank"
      rel="noopener noreferrer"
      className="tw-text-xs tw-font-normal tw-text-primary-blue tw-underline"
    >
      Learn more
    </a>
  </span>
);

const DescriptionForEvent: React.FC<{
  meetingType: 'zoom' | 'googleMeet';
  userFullName: string;
}> = ({ meetingType, userFullName }) => {
  return (
    <span className="tw-mt-1">
      Attendance will be tracked for {userFullName}’s {meetingType === 'zoom' ? 'Zoom' : 'Google'}{' '}
      meeting
    </span>
  );
};

const EventVirtualLocationField: React.FC<EventVirtualLocationFieldProps> = ({
  name,
  label = DefaultLabel,
  description = DefaultDescription,
  placeholder = 'https://',
  disabled = false,
  className,
  eventId,
  timezoneCode,
  meetingIdFieldNames = {
    zoom: 'zoomMeetingId',
    googleMeet: 'googleMeetingId'
  }
}) => {
  const { control, setValue, getValues } = useFormContext();

  const virtualLinkValue = useWatch({ control, name });
  const zoomMeetingIdValue = useWatch({ control, name: meetingIdFieldNames.zoom });
  const googleMeetingIdValue = useWatch({ control, name: meetingIdFieldNames.googleMeet });
  const timezoneCodeFormValue = useWatch({ control, name: 'timezoneCode' });

  const finalTimezoneCode = timezoneCode || timezoneCodeFormValue;

  const { showToast } = useToaster();
  const {
    hasCompletedZoomSetup,
    hasGoogleMeetIntegration,
    firstName,
    lastName,
    companyUsesGoogleCalendar
  } = useContext(UserContext) || {};

  const [modalState, setModalState] = useState<{
    type: ModalType | null;
    mode: ModalMode | null;
  }>({ type: null, mode: null });

  const [isCreatingZoomLink, setIsCreatingZoomLink] = useState(false);
  const [isCreatingMeetLink, setIsCreatingMeetLink] = useState(false);
  const [isRemovingLink, setIsRemovingLink] = useState(false);
  const [removeLinkConfirmModalOpen, setRemoveLinkConfirmModalOpen] = useState(false);

  const [isDuplicateLink, setIsDuplicateLink] = useState(false);
  const latestDuplicateCheckIdRef = useRef(0);

  const isFieldDisabled = disabled || isCreatingZoomLink || isCreatingMeetLink || isRemovingLink;
  const isInputDisabled = isFieldDisabled || !!(zoomMeetingIdValue || googleMeetingIdValue);

  const eventWithGeneratedMeeting = !!eventId && (zoomMeetingIdValue || googleMeetingIdValue);
  const showDescription = eventWithGeneratedMeeting || !!description;

  const [createZoomLinkMutation] = useMutation(CREATE_ZOOM_LINK, {
    onCompleted: (data) => {
      if (data.createZoomLink?.ok && data.createZoomLink?.link) {
        setValue(name, data.createZoomLink.link, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue(meetingIdFieldNames.zoom, data.createZoomLink.zoomMeetingId, {
          shouldValidate: false,
          shouldDirty: true
        });
        setValue(meetingIdFieldNames.googleMeet, undefined, { shouldValidate: false });
        showToast('Zoom meeting link generated!', { variant: ToasterVariant.DEFAULT });
      } else {
        showToast('Failed to generate Zoom link.', { variant: ToasterVariant.ERROR });
      }
      setIsCreatingZoomLink(false);
    },
    onError: (error) => {
      if (error.graphQLErrors?.[0]?.message === ZOOM_REINTEGRATION_ERROR) {
        setModalState({ type: ModalType.ZOOM, mode: ModalMode.RECONNECT });
      } else {
        showToast(error.message || DEFAULT_LINK_GENERATION_ERROR, {
          variant: ToasterVariant.ERROR
        });
      }
      setIsCreatingZoomLink(false);
    }
  });

  const handleGenerateZoomLink = (): void => {
    if (!hasCompletedZoomSetup) {
      setModalState({ type: ModalType.ZOOM, mode: ModalMode.CONNECT });
      return;
    }
    setIsCreatingZoomLink(true);

    const formValues = getValues();

    if (
      !formValues.title ||
      !formValues.startDateTime ||
      !formValues.endDateTime ||
      !finalTimezoneCode
    ) {
      showToast(
        'Event details (title, start/end times, timezone) are required to generate a Zoom link.',
        { variant: ToasterVariant.DEFAULT }
      );
      setIsCreatingZoomLink(false);
      return;
    }

    const variables = {
      title: formValues.title,
      description: formValues.description || '',
      datetimeScheduled: dayjs(formValues.startDateTime).format(FULL_DATE_FORMAT),
      datetimeEnd: dayjs(formValues.endDateTime).format(FULL_DATE_FORMAT),
      timezoneCode: finalTimezoneCode
    };
    createZoomLinkMutation({ variables });
  };

  const [createMeetLinkMutation] = useMutation(CREATE_MEET_LINK, {
    onCompleted: (data) => {
      if (data.createMeetLink?.ok && data.createMeetLink?.meetLink) {
        setValue(name, data.createMeetLink.meetLink, {
          shouldValidate: true,
          shouldDirty: true
        });
        setValue(meetingIdFieldNames.googleMeet, data.createMeetLink.googleMeetingId, {
          shouldValidate: false,
          shouldDirty: true
        });
        setValue(meetingIdFieldNames.zoom, undefined, { shouldValidate: false });
        showToast('Google Meet link generated!', { variant: ToasterVariant.DEFAULT });
      } else {
        showToast('Failed to generate Google Meet link.', { variant: ToasterVariant.ERROR });
      }
      setIsCreatingMeetLink(false);
    },
    onError: (error) => {
      if (error.graphQLErrors?.[0]?.message === MEET_REINTEGRATION_ERROR) {
        setModalState({ type: ModalType.MEET, mode: ModalMode.RECONNECT });
      } else {
        showToast(error.message || DEFAULT_LINK_GENERATION_ERROR, {
          variant: ToasterVariant.ERROR
        });
      }
      setIsCreatingMeetLink(false);
    }
  });

  const handleGenerateMeetLink = (): void => {
    if (!hasGoogleMeetIntegration) {
      setModalState({ type: ModalType.MEET, mode: ModalMode.CONNECT });
      return;
    }
    setIsCreatingMeetLink(true);
    const formValues = getValues();

    if (
      !formValues.title ||
      !formValues.startDateTime ||
      !formValues.endDateTime ||
      !finalTimezoneCode
    ) {
      showToast(
        'Event details (title, start/end times, timezone) are required to generate a Google Meet link.',
        { variant: ToasterVariant.DEFAULT }
      );
      setIsCreatingMeetLink(false);
      return;
    }

    createMeetLinkMutation({ variables: {} });
  };

  const [removeZoomLinkMutation] = useMutation(REMOVE_ZOOM_LINK, {
    onCompleted: (data) => {
      if (data.removeZoomLink?.ok) {
        setValue(name, '', { shouldValidate: true, shouldDirty: true });
        setValue(meetingIdFieldNames.zoom, undefined, { shouldValidate: false });
        showToast('Zoom link removed.', { variant: ToasterVariant.DEFAULT });
      } else {
        showToast('Failed to remove Zoom link.', { variant: ToasterVariant.ERROR });
      }
      setIsRemovingLink(false);
    },
    onError: (error) => {
      showToast(error.message, { variant: ToasterVariant.ERROR });
      setIsRemovingLink(false);
    }
  });

  const [removeMeetLinkMutation] = useMutation(REMOVE_MEET_LINK, {
    onCompleted: (data) => {
      if (data.removeMeetLink?.ok) {
        setValue(name, '', { shouldValidate: true, shouldDirty: true });
        setValue(meetingIdFieldNames.googleMeet, undefined, { shouldValidate: false });
        showToast('Google Meet link removed.', { variant: ToasterVariant.DEFAULT });
      } else {
        showToast('Failed to remove Google Meet link.', { variant: ToasterVariant.ERROR });
      }
      setIsRemovingLink(false);
    },
    onError: (error) => {
      showToast(error.message, { variant: ToasterVariant.ERROR });
      setIsRemovingLink(false);
    }
  });

  const handleRemoveVirtualLink = (): void => {
    setIsDuplicateLink(false);
    const formValues = getValues();
    const currentVirtualLinkVal = formValues[name];
    const currentZoomIdVal = formValues[meetingIdFieldNames.zoom];
    const currentGoogleIdVal = formValues[meetingIdFieldNames.googleMeet];

    setIsRemovingLink(true);
    if (currentZoomIdVal && currentVirtualLinkVal?.includes('zoom.us')) {
      removeZoomLinkMutation({ variables: { zoomMeetingId: currentZoomIdVal, eventId } });
    } else if (currentGoogleIdVal && currentVirtualLinkVal?.includes('meet.google.com')) {
      removeMeetLinkMutation({ variables: { googleMeetingId: currentGoogleIdVal } });
    } else {
      setValue(name, '', { shouldValidate: true, shouldDirty: true });
      setValue(meetingIdFieldNames.zoom, undefined, { shouldValidate: false });
      setValue(meetingIdFieldNames.googleMeet, undefined, { shouldValidate: false });
      setIsRemovingLink(false);
    }
  };

  const handleVirtualLinkBlur = (url: string): void => {
    if (!url) return;
    if (zoomMeetingIdValue || googleMeetingIdValue) return;

    const checkId = ++latestDuplicateCheckIdRef.current;

    const applyDuplicateResult = (checkedUrl: string, isDuplicate: boolean): void => {
      const currentUrl = getValues(name);
      const hasGeneratedMeeting =
        !!getValues(meetingIdFieldNames.zoom) || !!getValues(meetingIdFieldNames.googleMeet);
      if (checkId !== latestDuplicateCheckIdRef.current) return;
      if (currentUrl !== checkedUrl) return;
      if (hasGeneratedMeeting) return;
      setIsDuplicateLink(isDuplicate);
    };

    if (url.includes('zoom.us')) {
      isZoomLinkUsed(url)
        .then((isDuplicate) => applyDuplicateResult(url, isDuplicate))
        .catch(() => applyDuplicateResult(url, false));
    } else if (url.includes('meet.google.com')) {
      isGoogleMeetLinkUsed(url)
        .then((isDuplicate) => applyDuplicateResult(url, isDuplicate))
        .catch(() => applyDuplicateResult(url, false));
    }
  };

  return (
    <>
      <div className={className}>
        <FormField
          control={control}
          name={name}
          render={({ field }) => (
            <FormItem className="tw-mt-0">
              {label && <FormLabel className="tw-mb-0">{label}</FormLabel>}
              {showDescription && (
                <FormDescription className="tw-mt-1">
                  {eventWithGeneratedMeeting ? (
                    <DescriptionForEvent
                      meetingType={zoomMeetingIdValue ? 'zoom' : 'googleMeet'}
                      userFullName={`${firstName} ${lastName}`}
                    />
                  ) : (
                    description
                  )}
                </FormDescription>
              )}
              <FormControl>
                <div className="tw-relative">
                  <Input
                    {...field}
                    value={field.value || ''}
                    placeholder={placeholder}
                    disabled={isInputDisabled}
                    className={cn('tw-mt-2', virtualLinkValue && 'tw-text-ellipsis tw-pr-12')}
                    onBlur={(e): void => {
                      field.onBlur();
                      handleVirtualLinkBlur(e.target.value);
                    }}
                    onChange={(e): void => {
                      field.onChange(e);
                      latestDuplicateCheckIdRef.current += 1;
                      setIsDuplicateLink(false);
                    }}
                  />
                  {virtualLinkValue && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="tw-absolute tw-right-4 tw-top-0 tw-p-0 hover:tw-bg-transparent focus:tw-ring-0 focus:tw-ring-offset-0"
                      onClick={(): void => {
                        if (eventWithGeneratedMeeting) {
                          setRemoveLinkConfirmModalOpen(true);
                        } else {
                          handleRemoveVirtualLink();
                        }
                      }}
                      disabled={isFieldDisabled}
                      aria-label="Clear virtual link"
                    >
                      {isRemovingLink ? (
                        <Loader2 size={24} className="tw-animate-spin tw-text-negative-red" />
                      ) : (
                        <Trash2 size={24} className="tw-text-negative-red" />
                      )}
                    </Button>
                  )}
                </div>
              </FormControl>
              {isDuplicateLink && !zoomMeetingIdValue && !googleMeetingIdValue && (
                <AlertBanner variant="warning" className="tw-mt-2">
                  <AlertBannerIcon>
                    <Info size={16} />
                  </AlertBannerIcon>
                  <AlertBannerDescription>
                    This link was generated for another event. Attendance will be tracked only for
                    that event.
                  </AlertBannerDescription>
                </AlertBanner>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        {!zoomMeetingIdValue && !googleMeetingIdValue && (
          <div className="tw-mt-2 tw-flex tw-gap-x-2">
            <Button
              type="button"
              variant="secondary"
              size="xxs"
              className="tw-rounded-xl"
              onClick={handleGenerateZoomLink}
              disabled={isFieldDisabled}
              loading={isCreatingZoomLink}
            >
              <Plus size={16} className="tw-mr-1" />
              <span className="tw-ml-1">Add Zoom meeting</span>
            </Button>
            {companyUsesGoogleCalendar && (
              <Button
                type="button"
                variant="secondary"
                size="xxs"
                className="tw-rounded-xl"
                onClick={handleGenerateMeetLink}
                disabled={isFieldDisabled}
                loading={isCreatingMeetLink}
              >
                <Plus size={16} className="tw-mr-1" />
                <span className="tw-ml-1">Add Google Meet meeting</span>
              </Button>
            )}
          </div>
        )}
      </div>

      <ZoomConnectionModal
        open={modalState.type === ModalType.ZOOM}
        isReconnect={modalState.mode === ModalMode.RECONNECT}
        redirectTo={window.location.pathname}
        onOpenChange={(open): void => {
          if (!open) {
            setModalState({ type: null, mode: null });
          }
        }}
      />
      <GoogleMeetConnectionModal
        open={modalState.type === ModalType.MEET}
        isReconnect={modalState.mode === ModalMode.RECONNECT}
        redirectTo={window.location.pathname}
        onOpenChange={(open): void => {
          if (!open) {
            setModalState({ type: null, mode: null });
          }
        }}
      />

      <AlertDialog open={removeLinkConfirmModalOpen} onOpenChange={setRemoveLinkConfirmModalOpen}>
        <AlertDialogContent className="tw-max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete link?</AlertDialogTitle>
          </AlertDialogHeader>
          <p>Attendance for this event won’t be tracked automatically if you remove this link.</p>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleRemoveVirtualLink}
              disabled={isRemovingLink}
            >
              Delete link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export { EventVirtualLocationField };
